import { Audio, ExportFormat } from "@apis:audio/audio";
import { Copy } from "@components:Copy";
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { languageLabel } from "@pages:app/audio/components/language";
import { downloadJSON, downloadText, formatFileSize } from "@utils:file";
import { MouseEvent, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdAdd, MdExpandMore, MdInfoOutline } from "react-icons/md";

interface ResultActionsProps {
  file: File | null;
  title: string;
  requestedLanguage: string;
  requestedSpeakers: number | null;
  taskId: string;
  transcript: string;
  result: Audio["GET"]["Response"] | undefined;
  onExport: (format: ExportFormat) => void;
  isExporting?: boolean;
  onNew: () => void;
}

/*
 * What can be done with a finished transcription.
 * Technical details (task ID, requested settings) sit behind the info button.
 */
export const ResultActions = ({
  file,
  title,
  requestedLanguage,
  requestedSpeakers,
  taskId,
  transcript,
  result,
  onExport,
  isExporting = false,
  onNew,
}: ResultActionsProps) => {
  const { t } = useTranslation();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [detailsAnchor, setDetailsAnchor] = useState<HTMLElement | null>(null);

  const downloadTranscript = () => {
    setMenuAnchor(null);
    downloadText(transcript, `${title}.txt`);
  };

  const downloadResult = () => {
    setMenuAnchor(null);
    if (result) downloadJSON(result.result, `${title}-${result.stage}.json`);
  };

  const exportAs = (format: ExportFormat) => {
    setMenuAnchor(null);
    onExport(format);
  };

  return (
    <>
      <Tooltip title={t("audio.result.details")}>
        <IconButton
          aria-label={t("audio.result.detailsAria")}
          onClick={(event: MouseEvent<HTMLElement>) =>
            setDetailsAnchor(event.currentTarget)
          }
        >
          <MdInfoOutline />
        </IconButton>
      </Tooltip>
      <Popover
        anchorEl={detailsAnchor}
        open={!!detailsAnchor}
        onClose={() => setDetailsAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { p: 2, borderRadius: "12px" } } }}
      >
        <Stack spacing={1.5} sx={{ minWidth: 280 }}>
          <Detail label={t("audio.result.taskId")}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography
                variant="body2"
                sx={{ fontFamily: "monospace", overflowWrap: "anywhere" }}
              >
                {taskId}
              </Typography>
              <Copy text={taskId} tooltipText={t("audio.result.copyTaskId")} />
            </Stack>
          </Detail>
          <Detail label={t("audio.result.file")}>
            <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
              {file?.name ?? t("audio.recording.fallbackName")}
              {file ? ` (${formatFileSize(file.size)})` : ""}
            </Typography>
          </Detail>
          <Detail label={t("audio.result.languageRequested")}>
            <Typography variant="body2">
              {languageLabel(requestedLanguage, t) ??
                t("audio.languages.autodetect")}
            </Typography>
          </Detail>
          <Detail label={t("audio.result.speakersRequested")}>
            <Typography variant="body2">
              {requestedSpeakers ?? t("audio.languages.autodetect")}
            </Typography>
          </Detail>
        </Stack>
      </Popover>

      <Button
        variant="outlined"
        endIcon={<MdExpandMore />}
        onClick={(event: MouseEvent<HTMLElement>) =>
          setMenuAnchor(event.currentTarget)
        }
        aria-haspopup="menu"
        aria-expanded={menuAnchor ? "true" : undefined}
      >
        {t("audio.result.download.button")}
      </Button>
      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={() => exportAs("docx")} disabled={isExporting}>
          {t("audio.result.download.word")}
        </MenuItem>
        <MenuItem onClick={() => exportAs("pdf")} disabled={isExporting}>
          {t("audio.result.download.pdf")}
        </MenuItem>
        <MenuItem onClick={downloadTranscript} disabled={!transcript}>
          {t("audio.result.download.text")}
        </MenuItem>
        <MenuItem onClick={downloadResult} disabled={!result}>
          {t("audio.result.download.json")}
        </MenuItem>
      </Menu>

      <Button variant="outlined" startIcon={<MdAdd />} onClick={onNew}>
        {t("audio.result.newTranscription")}
      </Button>
    </>
  );
};

const Detail = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <Box>
    <Typography
      sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary", mb: 0.25 }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);
